/**
 * Video Preview Player Component (canvas-v2)
 *
 * Displays the current frame at the playhead position.
 * Handles client-side video sequencing across multiple clips.
 * Switches between clips based on playhead position and trim points.
 *
 * Multi-track: one <audio> per non-muted audio clip at its playbackRate and volume,
 * playing when playhead is within range.
 */

import { useRef, useEffect, useState, useMemo } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'
import { buildProjectAssetUrl } from '../../../../api/client'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'

interface VideoPreviewPlayerProps {
  /** Current clips on timeline */
  clips: TimelineClip[]

  /** Audio clips on timeline */
  audioClips: TimelineAudioClip[]

  /** Current playhead position (seconds) */
  playheadPosition: number

  /** Project ID for constructing video URLs */
  projectId: string | undefined

  /** Is video currently playing */
  isPlaying: boolean

  /** Callback when video ends */
  onVideoEnd?: () => void
}

interface ClipWithTiming extends TimelineClip {
  /** Start time in final timeline (seconds) */
  timelineStart: number

  /** End time in final timeline (seconds) */
  timelineEnd: number
}

/** Tracks audio element refs for multi-track playback */
interface AudioTrackRef {
  element: HTMLAudioElement
  clipId: string
}

export function VideoPreviewPlayer({
  clips,
  audioClips,
  playheadPosition,
  projectId,
  isPlaying,
  onVideoEnd,
}: VideoPreviewPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioTrackRefs = useRef<AudioTrackRef[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [currentClipIndex, setCurrentClipIndex] = useState<number | null>(null)

  // Compute clip timings (start/end times in the final timeline)
  const clipsWithTiming: ClipWithTiming[] = useMemo(() => {
    let cumulativeTime = 0
    return clips
      .sort((a, b) => a.order - b.order)
      .map((clip) => {
        const clipWithTiming: ClipWithTiming = {
          ...clip,
          timelineStart: cumulativeTime,
          timelineEnd: cumulativeTime + clip.effectiveDuration,
        }
        cumulativeTime += clip.effectiveDuration
        return clipWithTiming
      })
  }, [clips])

  // Find which clip should be shown at current playhead position
  const activeClip = useMemo(() => {
    return clipsWithTiming.find(
      (clip) => playheadPosition >= clip.timelineStart && playheadPosition < clip.timelineEnd
    )
  }, [clipsWithTiming, playheadPosition])

  // Build video URL for active clip
  const baseVideoUrl = activeClip && projectId
    ? buildProjectAssetUrl(projectId, activeClip.sourcePath)
    : null
  const videoUrl = useAuthenticatedMediaUrl(baseVideoUrl)

  // Compute active audio clips (those whose range covers the playhead)
  const activeAudioClips = useMemo(() => {
    return audioClips.filter((c) => {
      if (c.muted) return false
      const clipEnd = c.startOffset + c.effectiveDuration
      return playheadPosition >= c.startOffset && playheadPosition < clipEnd
    })
  }, [audioClips, playheadPosition])

  // Update current clip index when active clip changes
  useEffect(() => {
    if (activeClip) {
      const index = clipsWithTiming.findIndex((c) => c.id === activeClip.id)
      setCurrentClipIndex(index)
    } else {
      setCurrentClipIndex(null)
    }
  }, [activeClip, clipsWithTiming])

  // Track last active clip to detect clip transitions during playback
  const lastActiveClipIdRef = useRef<string | null>(null)
  const seekedHandlerRef = useRef<(() => void) | null>(null)

  // Sync video element with playhead position
  useEffect(() => {
    const video = videoRef.current
    if (!video || !activeClip) return

    // Clean up any pending seeked handler from a previous clip transition
    if (seekedHandlerRef.current) {
      video.removeEventListener('seeked', seekedHandlerRef.current)
      seekedHandlerRef.current = null
    }

    // Set video playback rate to clip speed
    video.playbackRate = activeClip.speed

    if (isPlaying) {
      // During playback, only seek when the active clip changes
      if (lastActiveClipIdRef.current !== activeClip.id) {
        // Wait for seek to complete before playing to avoid overlap
        video.pause()
        video.currentTime = activeClip.trimStart
        lastActiveClipIdRef.current = activeClip.id

        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked)
          seekedHandlerRef.current = null
          if (isPlaying) {
            video.play().catch((err) => {
              console.error('Video play after seek failed:', err)
            })
          }
        }
        seekedHandlerRef.current = onSeeked
        video.addEventListener('seeked', onSeeked)
      }
      // Otherwise let video play naturally
      return () => {
        if (seekedHandlerRef.current && video) {
          video.removeEventListener('seeked', seekedHandlerRef.current)
          seekedHandlerRef.current = null
        }
      }
    }

    // When NOT playing (scrubbing), seek to match playhead
    lastActiveClipIdRef.current = activeClip.id
    const relativeTime = playheadPosition - activeClip.timelineStart
    const videoTime = activeClip.trimStart + relativeTime
    if (Math.abs(video.currentTime - videoTime) > 0.05) {
      video.currentTime = videoTime
    }
  }, [playheadPosition, activeClip, isPlaying])

  // Multi-track audio: manage one <audio> element per non-muted clip
  useEffect(() => {
    // Clean up old audio elements
    audioTrackRefs.current.forEach((ref) => {
      ref.element.pause()
      ref.element.src = ''
      ref.element.remove()
    })
    audioTrackRefs.current = []

    if (!projectId) return

    // Create audio elements for active audio clips
    activeAudioClips.forEach((clip) => {
      const audio = document.createElement('audio')
      const baseUrl = buildProjectAssetUrl(projectId, clip.sourcePath)
      audio.src = baseUrl
      audio.preload = 'auto'
      audio.volume = clip.volume
      audio.playbackRate = clip.speed
      audio.muted = clip.muted

      // Seek to correct position
      const audioTime = clip.trimStart + (playheadPosition - clip.startOffset)
      if (audioTime >= 0) {
        audio.currentTime = audioTime
      }

      if (isPlaying) {
        audio.play().catch((err) => {
          console.error('Audio play failed:', err)
        })
      }

      audioTrackRefs.current.push({ element: audio, clipId: clip.id })
    })

    return () => {
      audioTrackRefs.current.forEach((ref) => {
        ref.element.pause()
        ref.element.src = ''
      })
      audioTrackRefs.current = []
    }
    // We intentionally only re-create audio elements when activeAudioClips identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAudioClips, isPlaying, projectId])

  // Handle play/pause for video
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying && activeClip) {
      video.play().catch((err) => {
        console.error('Video play failed:', err)
      })
    } else {
      video.pause()
    }
  }, [isPlaying, activeClip])

  // Monitor video time with RAF for frame-accurate clip boundary detection
  const boundaryRafRef = useRef<number | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !isPlaying || !activeClip) {
      if (boundaryRafRef.current !== null) {
        cancelAnimationFrame(boundaryRafRef.current)
        boundaryRafRef.current = null
      }
      return
    }

    const clipEnd = activeClip.trimStart + activeClip.effectiveDuration

    const checkBoundary = () => {
      const videoTime = video.currentTime

      if (videoTime >= clipEnd) {
        // Move to next clip
        const nextClipIndex = clipsWithTiming.findIndex((c) => c.id === activeClip.id) + 1

        if (nextClipIndex < clipsWithTiming.length) {
          video.pause()
        } else {
          // End of timeline
          video.pause()
          if (onVideoEnd) {
            onVideoEnd()
          }
        }
        return
      }

      boundaryRafRef.current = requestAnimationFrame(checkBoundary)
    }

    boundaryRafRef.current = requestAnimationFrame(checkBoundary)

    return () => {
      if (boundaryRafRef.current !== null) {
        cancelAnimationFrame(boundaryRafRef.current)
        boundaryRafRef.current = null
      }
    }
  }, [isPlaying, activeClip, clipsWithTiming, onVideoEnd])

  // Reset loading/error states when URL changes
  useEffect(() => {
    if (videoUrl) {
      setIsLoading(true)
      setHasError(false)
    }
  }, [videoUrl])

  const handleLoadedData = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)
    console.error('Video load error for clip:', activeClip?.label)
  }

  return (
    <div className="relative w-full h-full bg-black rounded-lg overflow-hidden border border-border">
      {videoUrl && activeClip ? (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-contain"
            onLoadedData={handleLoadedData}
            onError={handleError}
            playsInline
            preload="auto"
          />

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="text-center">
                <div className="w-12 h-12 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-text-secondary text-sm">Loading clip...</p>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center">
                <svg className="w-12 h-12 text-red-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-400 text-sm">Failed to load video</p>
                <p className="text-text-muted text-xs mt-1">{activeClip.label}</p>
              </div>
            </div>
          )}

          {/* Clip info overlay */}
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg border border-white/10">
            <p className="text-foreground text-xs font-medium">{activeClip.label}</p>
            <p className="text-text-muted text-xxs">
              Clip {(currentClipIndex ?? 0) + 1} of {clips.length}
              {activeClip.speed !== 1.0 && ` | ${activeClip.speed}x`}
            </p>
          </div>
        </>
      ) : (
        /* No active clip / end of timeline */
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 text-foreground/20 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-foreground/30 text-sm">
              {clips.length === 0 ? 'No clips on timeline' : 'End of timeline'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
